import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CopyAllIcon from '@mui/icons-material/CopyAll'

import {
  KNOWLEDGE_CONVERSATION_FLOW,
  KNOWLEDGE_KEYPHRASES,
  KNOWLEDGE_IMPORT_LMS_CONTENT,
} from '@/config'
import integrationService from '@/services/IntegrationService'
import contextialiseChatImg from '@/assets/New-images/contextualised-chat-img.svg'
import learningOutcomesImg from '@/assets/New-images/learning-outcomes-img.svg'
import manageConversationFlow from '@/assets/New-images/manage-conversation-flow.svg'
import mangeKeyPhrase from '@/assets/New-images/manage-key-phrase.svg'
import summariseDocImg from '@/assets/New-images/summarise-doc-img.svg'
import { ExploreButton } from '../ExploreButton'
import { PureCarousel } from '../PureCarousel'

function KnowledgeCarousel(): JSX.Element {
  const { t } = useTranslation('translation')
  const [hasLmsIntegration, setHasLmsIntegration] = useState(false)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const res = await integrationService.getIntegrations(null, 'api')

        const integrations = res?.data?.data?.integrations || []

        // Check if Moodle or Canvas is connected
        const hasMoodle = integrations.some(
          (integration: any) =>
            integration?.provider === 'moodle' &&
            integration?.params?.is_connected === true,
        )

        const hasCanvas = integrations.some(
          (integration: any) =>
            integration?.provider === 'canvas' &&
            integration?.params?.is_connected === true &&
            integration?.params?.enables_course_import === true,
        )

        if (mounted) setHasLmsIntegration(hasMoodle || hasCanvas)
      } catch (error) {
        console.error('Error fetching integrations:', error)
        if (mounted) setHasLmsIntegration(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  const CAROUSEL_DATA = [
    {
      name: t('Set Up Contextualized Chat'),
      description: t(
        'Upload your content to serve as sources for your AI assistant’s responses',
      ),
      icon: contextialiseChatImg,
      route: `/knowledge/setup/chat`,
    },
    {
      name: t('Set Learning Outcomes'),
      description: t('Align generated content by setting learning outcomes'),
      icon: learningOutcomesImg,
      route: `/knowledge/setup/learning-outcomes`,
    },
    {
      name: t('Summarize Document(s)'),
      description: t(
        'Create contents by summarising or merging existing documents',
      ),
      icon: summariseDocImg,
      route: `/knowledge/setup/summarise-doc`,
    },
    {
      name: t('Manage Key Phrases'),
      description: t(
        'Define and link key phrases to improve your contextual responses',
      ),
      icon: mangeKeyPhrase,
      route: KNOWLEDGE_KEYPHRASES,
    },
    {
      name: t('Manage Conversation Flow'),
      description: t(
        'Set up and connect content for a tailored chat experience',
      ),
      icon: manageConversationFlow,
      route: KNOWLEDGE_CONVERSATION_FLOW,
    },
  ]

  // Insert Import LMS Content before Manage Conversation Flow when integrated
  const carouselItems = (() => {
    if (!hasLmsIntegration) return CAROUSEL_DATA
    const insertionIndex = CAROUSEL_DATA.findIndex(
      item => item.route === KNOWLEDGE_CONVERSATION_FLOW,
    )
    const importItem = {
      name: t('Import LMS Content'),
      description: t('Import content from your integrated LMS.'),
      icon: <CopyAllIcon fontSize="small" color="primary" />, // set to blue via theme primary
      route: KNOWLEDGE_IMPORT_LMS_CONTENT,
    }
    if (insertionIndex === -1) return [...CAROUSEL_DATA, importItem]
    return [
      ...CAROUSEL_DATA.slice(0, insertionIndex),
      importItem,
      ...CAROUSEL_DATA.slice(insertionIndex),
    ]
  })()

  return (
    <div>
      <PureCarousel>
        {carouselItems.map((item, index) => {
          return (
            <ExploreButton
              key={index}
              title={item.name}
              route={item.route}
              icon={item.icon}
              fullWidth
            />
          )
        })}
      </PureCarousel>
    </div>
  )
}

export default KnowledgeCarousel
